import { usePathname, useRouter, useSearchParams } from "next/navigation"
import * as React from "react"

import {
  createSearchParams,
  getSearchParamsForLocation,
  URLSearchParamsInit,
} from "@/lib/router-helpers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type SetURLSearchParams = (
  nextInit?:
    | URLSearchParamsInit
    | ((prev: URLSearchParams) => URLSearchParamsInit),
  options?: {
    scroll?: boolean
    replace?: boolean
  },
) => void

/* -------------------------------------------------------------------------------------------------
 * hook
 * -----------------------------------------------------------------------------------------------*/

export function useCustomSearchParams(
  defaultInit?: URLSearchParamsInit,
): [URLSearchParams, SetURLSearchParams] {
  const _nextSearchParams = useSearchParams()
  const pathName = usePathname()
  const router = useRouter()

  const defaultSearchParamsRef = React.useRef(createSearchParams(defaultInit))
  const hasSetSearchParamsRef = React.useRef(false)

  const searchParams = React.useMemo(
    () =>
      getSearchParamsForLocation(
        _nextSearchParams.toString(),
        // eslint-disable-next-line react-hooks/refs
        hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current,
      ),
    [_nextSearchParams],
  )

  const setSearchParams = React.useCallback<SetURLSearchParams>(
    (nextInit, options) => {
      const newSearchParams = createSearchParams(
        typeof nextInit === "function" ? nextInit(searchParams) : nextInit,
      )
      hasSetSearchParamsRef.current = true

      if (options?.replace)
        router.replace(`${pathName}?${newSearchParams}`, {
          scroll: options.scroll,
        })
      else
        router.push(`${pathName}?${newSearchParams}`, {
          scroll: options?.scroll,
        })
    },
    [searchParams, pathName, router],
  )

  return [searchParams, setSearchParams]
}
